/**
 * VerticalSlider - Vrai slider vertical sans hack de rotation
 * Utilise des divs et du drag handling comme SkeuoKnob
 */

export class VerticalSlider {
    constructor(container, options = {}) {
        this.container = container;
        this.min = options.min ?? 0;
        this.max = options.max ?? 1;
        this.value = options.value ?? this.min;
        this.step = options.step ?? 0.01;
        this.label = options.label ?? '';
        this.showValue = options.showValue ?? true;
        this.height = options.height ?? 100;  // Hauteur du track en px
        this.variant = options.variant ?? 'fader';  // 'fader' ou 'send'

        this._callback = null;
        this._isDragging = false;

        this._render();
        this._setupInteraction();
    }

    _render() {
        this.element = document.createElement('div');
        this.element.className = `vslider vslider-${this.variant}`;

        this.element.innerHTML = `
            <div class="vslider-track" style="height: ${this.height}px">
                <div class="vslider-fill"></div>
                <div class="vslider-thumb"></div>
            </div>
            ${this.label ? `<span class="vslider-label">${this.label}</span>` : ''}
            ${this.showValue ? `<span class="vslider-value"></span>` : ''}
        `;

        this.track = this.element.querySelector('.vslider-track');
        this.fill = this.element.querySelector('.vslider-fill');
        this.thumb = this.element.querySelector('.vslider-thumb');
        this.valueDisplay = this.element.querySelector('.vslider-value');

        this.container.appendChild(this.element);
        this._updateVisual();
    }

    _setupInteraction() {
        // Mouse events
        this.thumb.addEventListener('mousedown', (e) => this._startDrag(e));
        this.track.addEventListener('mousedown', (e) => this._jumpToPosition(e));

        // Touch events
        this.thumb.addEventListener('touchstart', (e) => this._startDrag(e), { passive: false });
        this.track.addEventListener('touchstart', (e) => this._jumpToPosition(e), { passive: false });

        // Wheel support
        this.element.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -this.step : this.step;
            this._setValue(this.value + delta * 10);
        }, { passive: false });
    }

    _startDrag(e) {
        e.preventDefault();
        e.stopPropagation();
        this._isDragging = true;
        this.thumb.classList.add('active');

        const moveHandler = (e) => this._onDrag(e);
        const upHandler = () => {
            this._isDragging = false;
            this.thumb.classList.remove('active');
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', upHandler);
            document.removeEventListener('touchmove', moveHandler);
            document.removeEventListener('touchend', upHandler);
        };

        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', upHandler);
        document.addEventListener('touchmove', moveHandler, { passive: false });
        document.addEventListener('touchend', upHandler);
    }

    _onDrag(e) {
        if (!this._isDragging) return;
        e.preventDefault();

        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        this._setValueFromY(clientY);
    }

    _jumpToPosition(e) {
        if (e.target === this.thumb) return;
        e.preventDefault();

        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        this._setValueFromY(clientY);
        this._startDrag(e);
    }

    _setValueFromY(clientY) {
        const rect = this.track.getBoundingClientRect();
        // Inverser: haut = max, bas = min
        const percent = 1 - ((clientY - rect.top) / rect.height);
        const clampedPercent = Math.max(0, Math.min(1, percent));
        const newValue = this.min + clampedPercent * (this.max - this.min);
        this._setValue(newValue);
    }

    _setValue(newValue) {
        // Clamp et arrondir au step
        newValue = Math.max(this.min, Math.min(this.max, newValue));
        newValue = Math.round(newValue / this.step) * this.step;

        if (newValue !== this.value) {
            this.value = newValue;
            this._updateVisual();
            if (this._callback) {
                this._callback(this.value);
            }
        }
    }

    _updateVisual() {
        const percent = (this.value - this.min) / (this.max - this.min);
        const thumbPosition = (1 - percent) * (this.height - 20);  // 20 = thumb height approx

        this.thumb.style.top = `${thumbPosition}px`;
        this.fill.style.height = `${percent * 100}%`;

        if (this.valueDisplay) {
            this.valueDisplay.textContent = this._formatValue();
        }
    }

    _formatValue() {
        // Format intelligent selon la plage
        if (this.value === 0) return '-∞';
        if (this.max <= 1) {
            // Probablement un ratio 0-1, afficher en dB
            const db = 20 * Math.log10(this.value);
            return `${db.toFixed(1)} dB`;
        }
        return this.value.toFixed(2);
    }

    // API publique
    getValue() {
        return this.value;
    }

    setValue(value) {
        this._setValue(value);
        return this;
    }

    onChange(callback) {
        this._callback = callback;
        return this;
    }

    destroy() {
        this.element.remove();
    }
}
